import { useMemo, useState } from "react";
import { CalcSlider, StatPill, fmtINR } from "./CalcShared";
import { CALC } from "@/constants/testIds";

// Compute required monthly SIP for a goal: FV = SIP * (((1+r)^n - 1)/r) * (1+r)
function requiredSIP(target, years, ratePct) {
  const r = ratePct / 12 / 100;
  const n = years * 12;
  if (r === 0) return target / n;
  return target / (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
}

export default function GoalPlannerCalculator() {
  const [target, setTarget] = useState(2500000);
  const [years, setYears] = useState(12);
  const [rate, setRate] = useState(12);

  const result = useMemo(() => {
    const sip = Math.ceil(requiredSIP(target, years, rate));
    const invested = sip * years * 12;
    const returns = target - invested;
    return { sip, invested, returns };
  }, [target, years, rate]);

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid={CALC.goalCard}>
      <div className="space-y-6">
        <CalcSlider
          label="Goal target amount"
          value={target}
          onChange={setTarget}
          min={100000}
          max={50000000}
          step={50000}
          format={fmtINR}
          testId={CALC.goalAmount}
        />
        <CalcSlider
          label="Years to goal"
          value={years}
          onChange={setYears}
          min={1}
          max={35}
          step={1}
          format={(v) => `${v} yr`}
          testId={CALC.goalYears}
        />
        <CalcSlider
          label="Expected annual return"
          value={rate}
          onChange={setRate}
          min={4}
          max={20}
          step={0.5}
          format={(v) => `${v}%`}
          testId={CALC.goalRate}
        />
      </div>

      <div className="bg-muted/40 rounded-2xl p-6 flex flex-col justify-center">
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">You need to invest</div>
          <div className="font-serif text-4xl md:text-5xl text-primary mt-2" data-testid={CALC.goalResult}>
            {fmtINR(result.sip)}
          </div>
          <div className="text-sm text-foreground/65 mt-1">per month, for {years} years</div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-8">
          <StatPill label="Target" value={fmtINR(target)} tone="primary" />
          <StatPill label="Total Invested" value={fmtINR(result.invested)} />
          <StatPill label="Expected Returns" value={fmtINR(result.returns)} tone="secondary" />
        </div>
      </div>
    </div>
  );
}
